import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  LayoutDashboard,
  Hotel,
  MapPin,
  Package,
  BookOpen,
  BarChart3,
  Settings as SettingsIcon,
  Upload,
  Menu,
  X,
  LogOut,
  User,
  Globe,
  Code,
} from 'lucide-react'
import HotelManagement from './HotelManagement'
import ActivityManagement from './ActivityManagement'
import PackageManagement from './PackageManagement'
import BookingManagement from './BookingManagement'
import Analytics from './Analytics'
import SettingsComponent from './Settings'
import CrawlManagement from './CrawlManagement'
import SchemaValidation from './SchemaValidation'

interface DashboardProps {
  tenantId: string
  userRole: 'operator' | 'admin'
  onLogout?: () => void
}

const Dashboard: React.FC<DashboardProps> = ({ tenantId, userRole, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [user, setUser] = useState({
    name: 'Loading...',
    email: 'Loading...',
    company: 'Loading...'
  })
  const location = useLocation()

  useEffect(() => {
    fetchUserInfo()
  }, [])

  const fetchUserInfo = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/auth/me`)
      const data = await response.json()
      if (data.success && data.user) {
        setUser({
          name: data.user.full_name || data.user.email.split('@')[0],
          email: data.user.email,
          company: data.user.tenant_name || 'Unknown Company'
        })
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error)
    }
  }

  const navigation = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard, current: location.pathname === '/dashboard' },
    { name: 'Hotels', href: '/dashboard/hotels', icon: Hotel, current: location.pathname.includes('/hotels') },
    { name: 'Activities', href: '/dashboard/activities', icon: MapPin, current: location.pathname.includes('/activities') },
    { name: 'Packages', href: '/dashboard/packages', icon: Package, current: location.pathname.includes('/packages') },
    { name: 'Bookings', href: '/dashboard/bookings', icon: BookOpen, current: location.pathname.includes('/bookings') },
    { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3, current: location.pathname.includes('/analytics') },
    { name: 'Content', href: '/dashboard/content', icon: Globe, current: location.pathname.includes('/content') },
    { name: 'Schema.org', href: '/dashboard/schema', icon: Code, current: location.pathname.includes('/schema') },
    { name: 'Settings', href: '/dashboard/settings', icon: SettingsIcon, current: location.pathname.includes('/settings') },
  ]

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col`}>
        <div className="flex items-center justify-between h-16 px-6 bg-purple-600 text-white">
          <h1 className="text-xl font-bold">Booking Dashboard</h1>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-6 border-b">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">{user.name}</p>
              <p className="text-xs text-gray-500">{user.company}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3 flex-1">
          <div className="space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  item.current
                    ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } group flex items-center px-3 py-2 text-sm font-medium rounded-l-md transition-colors duration-150`}
              >
                <item.icon
                  className={`${
                    item.current ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'
                  } flex-shrink-0 -ml-1 mr-3 h-5 w-5`}
                  aria-hidden="true"
                />
                <span className="truncate">{item.name}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-gray-200">
          <button 
            onClick={onLogout}
            className="flex items-center w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors duration-150"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </button>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Tenant ID: {tenantId}
              </div>
              <div className="text-sm text-gray-500">
                Role: {userRole}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Routes>
            <Route path="/" element={<DashboardOverview tenantId={tenantId} />} />
            <Route path="/hotels/*" element={<HotelManagement tenantId={tenantId} />} />
            <Route path="/activities/*" element={<ActivityManagement tenantId={tenantId} />} />
            <Route path="/packages/*" element={<PackageManagement tenantId={tenantId} />} />
            <Route path="/bookings/*" element={<BookingManagement tenantId={tenantId} />} />
            <Route path="/analytics" element={<Analytics tenantId={tenantId} />} />
            <Route path="/content/*" element={<CrawlManagement tenantId={tenantId} />} />
            <Route path="/schema/*" element={<SchemaValidation tenantId={tenantId} />} />
            <Route path="/settings" element={<SettingsComponent tenantId={tenantId} />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

// Dashboard Overview Component
const DashboardOverview: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [stats, setStats] = useState({
    totalHotels: 0,
    totalActivities: 0,
    totalPackages: 0,
    totalBookings: 0,
    revenue: 0,
    pendingBookings: 0
  })
  const [recentBookings, setRecentBookings] = useState([])

  useEffect(() => {
    fetchDashboardStats()
    fetchRecentBookings()
  }, [tenantId])

  const fetchDashboardStats = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/stats?tenant_id=${tenantId}`)
      const data = await response.json()
      if (data.success) {
        setStats(data.stats)
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error)
    }
  }

  const fetchRecentBookings = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/recent-bookings?tenant_id=${tenantId}&limit=3`)
      const data = await response.json()
      if (data.success) {
        setRecentBookings(data.bookings)
      }
    } catch (error) {
      console.error('Failed to fetch recent bookings:', error)
    }
  }

  const statCards = [
    { name: 'Total Hotels', value: stats.totalHotels, icon: Hotel },
    { name: 'Total Activities', value: stats.totalActivities, icon: MapPin },
    { name: 'Total Packages', value: stats.totalPackages, icon: Package },
    { name: 'Total Bookings', value: stats.totalBookings, icon: BookOpen },
  ]

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with your travel business.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{stat.value}</div>
                    {(stat as any).change && (
                      <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                        (stat as any).changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(stat as any).change}
                      </div>
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/dashboard/hotels/new"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-150"
          >
            <Hotel className="h-6 w-6 text-purple-600 mr-3" />
            <span className="text-sm font-medium text-gray-900">Add New Hotel</span>
          </Link>
          <Link
            to="/dashboard/activities/new"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-150"
          >
            <MapPin className="h-6 w-6 text-purple-600 mr-3" />
            <span className="text-sm font-medium text-gray-900">Add New Activity</span>
          </Link>
          <Link
            to="/dashboard/packages/new"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-150"
          >
            <Package className="h-6 w-6 text-purple-600 mr-3" />
            <span className="text-sm font-medium text-gray-900">Create Package</span>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Bookings</h2>
        <div className="space-y-4">
          {recentBookings.length > 0 ? (
            recentBookings.map((booking: any, index: number) => (
              <div key={booking.booking_reference || index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                <div>
                  <p className="text-sm font-medium text-gray-900">{booking.item_name}</p>
                  <p className="text-xs text-gray-500">Booked by {booking.guest_name} • {booking.time_ago}</p>
                  <p className="text-xs text-purple-600">${booking.total_amount?.toLocaleString()} • {booking.item_type}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {booking.status}
                </span>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No recent bookings found</p>
              <p className="text-xs">Bookings will appear here once customers make reservations</p>
            </div>
          )}
        </div>
        <div className="mt-4">
          <Link
            to="/dashboard/bookings"
            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            View all bookings →
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Dashboard